/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   7/28/21
 */

import React from "react";
import { Stack, useTheme } from "@mui/material";
import { borderedSx, fixedSx, lastOfTypeY } from "./sx";
import EmptyCell from "./EmptyCell";
import EmployeeCell from "./EmployeeCell";
import {
	BaseEvent,
	Employee,
	GoogleEvent,
	Holiday,
	OpsGenieEvent,
	OverlapEvent,
	VegaEvent,
	WorkdayEvent
} from "../interfaces/Global";
import EventDialog from "./EventDialog";
import { addDays, areIntervalsOverlapping, setYear } from "date-fns";
import VegaViewportEvent from "./VegaViewportEvent";
import GoogleViewportEvent from "./GoogleViewportEvent";
import { useSelector } from "react-redux";
import { State } from "../state/store";
import OooViewportEvent from "./OooViewportEvent";
import EmployeeViewportEvent from "./EmployeeViewportEvent";
import HolidayViewportEvent from "./HolidayViewportEvent";
import WorkdayViewportEvent from "./WorkdayViewportEvent";
import OpsGenieViewportEvent from "./OpsGenieViewportEvent";
import { getOverlapInterval } from "../dates-helper";
import OverlapViewportEvent from "./OverlapViewportEvent";

export interface RowProps {
	range: Interval;
	employee: Employee;
	vegaEvents: VegaEvent[];
	googleEvents: GoogleEvent[];
	workdayEvents: WorkdayEvent[];
	opsGenieEvents: OpsGenieEvent[];
	intervals: Date[];
	intervalWidth: number;
	intervalDuration: number;
	rangeResolution: number;
	normalizeEventsPosition: boolean;
	oooEvents: Interval[];
	holidays: Holiday[];
}

export default function Row(props: RowProps) {

	const recentEventTypes = useSelector((s:State) => s.recentEventTypes)

	const [ eventDialogOpen, setEventDialogOpen ] = React.useState(false);
	const displayEmployeeBirthdays = useSelector((s: State) => s.settings.displayEmployeeBirthdays);
	const displayEmployeeAnniversaries = useSelector((s: State) => s.settings.displayEmployeeAnniversaries);
	const displayOverlapEvents = useSelector((s: State) => s.settings.displayOverlapEvents);
	const [ upsertEvent, setUpsertEvent ] = React.useState<Partial<VegaEvent>>({});
	const theme = useTheme();

	const openNewEventDialog = React.useCallback((event: Partial<VegaEvent>) => {
		setUpsertEvent({
			typeId: recentEventTypes[0] ?? "AL",
			...event,
			employeeId: props.employee.id,
		});
		setEventDialogOpen(true);
	}, [ props.employee.id, recentEventTypes ]);

	const openEditEventDialog = React.useCallback((event: VegaEvent) => {
		setUpsertEvent(event);
		setEventDialogOpen(true);
	}, []);

	const overlapEvents = React.useMemo(() => displayOverlapEvents ? getOverlapEvents({
		vegaEvents: props.vegaEvents,
		workdayEvents: props.workdayEvents,
		googleEvents: props.googleEvents,
		opsGenieEvents: props.opsGenieEvents,
	}): [], [displayOverlapEvents, props.googleEvents, props.opsGenieEvents, props.vegaEvents, props.workdayEvents]);

	return (
		<Stack direction={"row"} sx={{ ...lastOfTypeY(theme) }}>
			<Stack sx={{ ...fixedSx(theme), ...borderedSx(theme) }}>
				<EmployeeCell
					employee={props.employee}
				/>
			</Stack>
			<Stack>
				<Stack direction={"row"} sx={{ height: "100%" }}>
					{props.vegaEvents.map(event =>
						<VegaViewportEvent
							key={event.id}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							event={event}
							openEditEventDialog={openEditEventDialog}
						/>)
					}
					{props.googleEvents.map(event =>
						<GoogleViewportEvent
							key={event.id}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							event={event}
						/>)
					}
					{props.workdayEvents.map(event =>
						<WorkdayViewportEvent
							key={event.id}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							event={event}
						/>)
					}
					{props.opsGenieEvents.map(event =>
						<OpsGenieViewportEvent
							key={event.id}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							event={event}
						/>)
					}
					{props.oooEvents.map(e =>
						<OooViewportEvent
							key={e.start.toString()}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							event={e}
						/>)}
					{overlapEvents.map(e =>
						<OverlapViewportEvent
							key={e.id}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							event={e}
						/>)}
					{displayEmployeeBirthdays && props.employee.dateOfBirth
						&& areIntervalsOverlapping(
							props.range,
							{
								start: setYear(props.employee.dateOfBirth, new Date().getFullYear()),
								end: addDays(setYear(props.employee.dateOfBirth, new Date().getFullYear()), 1)
							}
						) &&
						<EmployeeViewportEvent
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							intervalDuration={props.intervalDuration}
							employee={props.employee}
							employeeAttribute={"dateOfBirth"}
							event={{
								start: setYear(props.employee.dateOfBirth, new Date().getFullYear()),
								end: addDays(setYear(props.employee.dateOfBirth, new Date().getFullYear()), 1)
							}}
							title={"Birthday"}
							openNewEventDialog={openNewEventDialog}
						/>}
					{displayEmployeeAnniversaries && props.employee.dateOfHire
						&& areIntervalsOverlapping(
							props.range,
							{
								start: setYear(props.employee.dateOfHire, new Date().getFullYear()),
								end: addDays(setYear(props.employee.dateOfHire, new Date().getFullYear()), 1)
							}
						) &&
						<EmployeeViewportEvent
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							intervalDuration={props.intervalDuration}
							employee={props.employee}
							employeeAttribute={"dateOfHire"}
							event={{
								start: setYear(props.employee.dateOfHire, new Date().getFullYear()),
								end: addDays(setYear(props.employee.dateOfHire, new Date().getFullYear()), 1)
							}}
							title={"Atlassian Anniversary"}
							openNewEventDialog={openNewEventDialog}
						/>}
					{props.holidays.map(h =>
						<HolidayViewportEvent
							key={h.id}
							viewportRange={props.range}
							rangeResolution={props.rangeResolution}
							intervalDuration={props.intervalDuration}
							employee={props.employee}
							event={h}
							openNewEventDialog={openNewEventDialog}
						/>)}
					{props.intervals.map(d =>
						<EmptyCell
							key={d.toISOString()}
							interval={d}
							intervalDuration={props.intervalDuration}
							rangeResolution={props.rangeResolution}
							employee={props.employee}
							openNewEventDialog={openNewEventDialog}
							width={props.intervalWidth}
						/>
					)}
				</Stack>
			</Stack>
			<EventDialog
				op={upsertEvent.id ? "update" : "create"}
				key={props.employee.id + (upsertEvent.id || 0) + upsertEvent.startDatetime?.getTime()}
				open={eventDialogOpen}
				onClose={() => setEventDialogOpen(false)}
				model={upsertEvent}
				employee={props.employee}
			/>
		</Stack>
	);

}

interface GetOverlapEventsConfig {
	vegaEvents: VegaEvent[];
	googleEvents: GoogleEvent[];
	workdayEvents: WorkdayEvent[];
	opsGenieEvents: OpsGenieEvent[];
}

function getOverlapEvents(config: GetOverlapEventsConfig): OverlapEvent[] {
	const events: OverlapEvent[] = [];
	function addOverlapEvents (events1: BaseEvent[], events2: BaseEvent[]) {
		for (let event1 of events1) {
			if (event1.properties?.loading) {
				continue;
			}
			for (let event2 of events2) {
				if (event2.properties?.loading || event1.id === event2.id) {
					continue;
				}
				const overlapInterval = getOverlapInterval(
					{ start: event1.startDatetime, end: event1.endDatetime },
					{ start: event2.startDatetime, end: event2.endDatetime }
				);
				if (overlapInterval) {
					const key = [ event1.id, event2.id ].join("-");
					const reverseKey = [ event2.id, event1.id ].join("-");
					if (!events.some(e => e.id === key || e.id === reverseKey)) {
						events.push({
							id: key,
							employeeId: event1.employeeId,
							startDatetime: new Date(overlapInterval.start),
							endDatetime: new Date(overlapInterval.end),
							typeId: "OVERLAP",
							events: [ event1, event2 ]
						});
					}
				}
			}
		}
	}
	addOverlapEvents(config.vegaEvents, config.vegaEvents);
	addOverlapEvents(config.vegaEvents, config.workdayEvents);
	addOverlapEvents(config.vegaEvents, config.googleEvents);
	addOverlapEvents(config.vegaEvents, config.opsGenieEvents);
	addOverlapEvents(config.workdayEvents, config.workdayEvents);
	addOverlapEvents(config.workdayEvents, config.googleEvents);
	addOverlapEvents(config.workdayEvents, config.opsGenieEvents);
	addOverlapEvents(config.googleEvents, config.googleEvents);
	addOverlapEvents(config.googleEvents, config.opsGenieEvents);
	return events;
}